import type React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { useTimerRef } from '@libs/typescript-utils'

import localeCompareSort from '@helpers/stringOrder'
import { applyOpacity } from '@helpers/styled-helpers'

import type { OnOpenChangeCallbackProps } from '@components/ui'
import {
  FilterClearIconButton,
  FilterSearchIcon,
  Input,
  List,
  LIST_ITEM_CSS_VARIABLES,
  ListItem,
  Popover,
  Typography,
} from '@components/ui'

import { FilterChip } from './FilterChip'
import type { CommonFilterSelectProps } from './types'

import messages from '@components/filters/PageFilterBars/messages'
import { ButtonLabel, ButtonValue, MenuContainer, StyledButtonBase } from '../styles'

const HeadingTypography = styled(Typography).attrs({
  tag: 'h4',
  size: '700-xs',
  color: 'gray-80',
})`
  text-transform: uppercase;
`

const SearchInput = styled(Input)`
  margin-bottom: 20px;
  --input-bg-color: ${({ theme }) => theme.colors.gray[102]};
  --input-focus-border-color: ${({ theme }) => theme.colors.common.oceanBlue};
  font-size: 12px;
`

const StyledMenuList = styled(List).attrs({
  menu: true,
  disablePadding: true,
})`
  ${LIST_ITEM_CSS_VARIABLES.listItemVerticalPadding}: 5px;
  ${LIST_ITEM_CSS_VARIABLES.listItemHorizontalPadding}: 20px;
  ${LIST_ITEM_CSS_VARIABLES.menuItemHoverColor}: ${({ theme }) => applyOpacity(theme.colors.blue[40], 6)};
  ${LIST_ITEM_CSS_VARIABLES.listItemTextColor}: ${({ theme }) => theme.colors.gray[100]};

  max-height: 180px;
  overflow: auto;
  margin-bottom: 20px;

  & > li {
    height: 30px;
    display: block;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    box-sizing: border-box;
  }
`

const InputContainer = styled.div`
  padding: 20px 20px 15px;
  border-top: 1px solid ${({ theme }) => theme.colors.common.paperStroke};
`

const SelectedFiltersContainer = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 10px;
`

interface WidgetFilterSelectProps<Payload extends Record<string, SelectableFilterOption[]>>
  extends CommonFilterSelectProps<Payload> {
  disabled?: boolean
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  value: SelectableFilterOption[]
}

export function WidgetFilterSelect<Payload extends Record<string, SelectableFilterOption[]>>({
  disabled = false,
  keyValue,
  labelText,
  onChange,
  searchTerm,
  selectableOptions,
  selectedLabelText,
  selectedOptions,
  setSearchTerm,
  value,
  emptyPlaceholder = '-',
}: WidgetFilterSelectProps<Payload>) {
  const { formatMessage } = useIntl()
  const timerRef = useTimerRef()

  const onOpenChangeCallback = ({ nextOpen, transitionDuration }: OnOpenChangeCallbackProps) => {
    if (!nextOpen) {
      // clear search input value when popover is closed
      timerRef.current = window.setTimeout(() => {
        setSearchTerm('')
      }, transitionDuration)
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const handleEscClear = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (searchTerm && event.key === 'Escape') {
      event.stopPropagation()
      setSearchTerm('')
    }
  }

  const handleSelect = (option: SelectableFilterOption) => () => {
    onChange({ [keyValue]: [...value, option] } as Payload) // need to cast due to generic type, otherwise TS will complain
  }

  const handleDeselect = (option: SelectableFilterOption) => () => {
    onChange({
      [keyValue]: value.filter(({ id }) => id !== option.id),
    } as Payload) // need to cast due to generic type, otherwise TS will complain
  }

  return (
    <>
      <Popover
        placement="bottom-start"
        onOpenChangeCallback={onOpenChangeCallback}
        trigger={
          <StyledButtonBase disabled={disabled} data-testid="widget-filter-select">
            <ButtonLabel>{labelText}</ButtonLabel>
            <ButtonValue>{selectedOptions.map(option => option.name).join(', ') || emptyPlaceholder}</ButtonValue>
          </StyledButtonBase>
        }
      >
        <MenuContainer>
          <HeadingTypography>
            <FormattedMessage
              id="filterSelect.popover.headings.selected"
              defaultMessage="Kijelölt {values}"
              values={{
                values: selectedLabelText || labelText,
              }}
            />
          </HeadingTypography>

          <SelectedFiltersContainer>
            {selectedOptions.length === 0 && <ButtonValue>{emptyPlaceholder}</ButtonValue>}
            {selectedOptions.map(option => (
              <FilterChip key={option.id} label={option.name} onDelete={handleDeselect(option)} />
            ))}
          </SelectedFiltersContainer>
        </MenuContainer>
        <InputContainer>
          <SearchInput
            placeholder={formatMessage(messages.searchInputPlaceholder)}
            value={searchTerm}
            onKeyDown={handleEscClear}
            onChange={handleInputChange}
            endAdornment={
              <>
                <FilterClearIconButton onClick={() => setSearchTerm('')} visible={Boolean(searchTerm)} />
                <FilterSearchIcon size={16} />
              </>
            }
          />

          <HeadingTypography>{labelText}</HeadingTypography>
        </InputContainer>
        <div>
          <StyledMenuList>
            {selectableOptions
              .sort((a, b) => localeCompareSort(a.name, b.name))
              .map(option => (
                <ListItem key={option.id} onClick={handleSelect(option)} menuItem>
                  <Typography size="400-sm">{option.name}</Typography>
                </ListItem>
              ))}
          </StyledMenuList>
        </div>
      </Popover>
    </>
  )
}
